import type { RenderedSavedReply, SavedReply, SavedReplyInput } from '@peles/shared';
import { apiClient } from './client';

/** `/saved-replies` — wording a producer inserts into any message they compose. */
const BASE = '/saved-replies';

/** The record a message is about, whose details fill a reply's merge tags. */
export interface MergeRecord {
  entityType: string;
  entityId: string;
}

export async function listSavedReplies(): Promise<SavedReply[]> {
  const { data } = await apiClient.get<SavedReply[]>(BASE);
  return data;
}

export async function createSavedReply(input: SavedReplyInput): Promise<SavedReply> {
  const { data } = await apiClient.post<SavedReply>(BASE, input);
  return data;
}

export async function updateSavedReply(
  id: string,
  input: Partial<SavedReplyInput>,
): Promise<SavedReply> {
  const { data } = await apiClient.patch<SavedReply>(`${BASE}/${id}`, input);
  return data;
}

export async function deleteSavedReply(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`);
}

/**
 * What a message is about: one record, several (the event and the one vendor
 * it goes to), or none. The first listed wins a tag two of them answer.
 */
export type MergeContext = MergeRecord | readonly MergeRecord[] | null | undefined;

export function mergeRecords(context: MergeContext): MergeRecord[] {
  if (!context) return [];
  const list: readonly MergeRecord[] = Array.isArray(context) ? context : [context as MergeRecord];
  return list.map(({ entityType, entityId }) => ({ entityType, entityId }));
}

/** The reply filled in against the records — or only the writer's own tags, with none. */
export async function renderSavedReply(
  id: string,
  context?: MergeContext,
): Promise<RenderedSavedReply> {
  const records = mergeRecords(context);
  const { data } = await apiClient.post<RenderedSavedReply>(
    `${BASE}/${id}/render`,
    records.length > 0 ? { records } : {},
  );
  return data;
}
